function getFontString(fontSize, fontFamily) {
    switch (fontFamily) {
        case 1: return `${fontSize}px Virgil`;
        case 3: return `${fontSize}px Cascadia`;
        case 2:
        default: return `${fontSize}px Helvetica, Arial, sans-serif`;
    }
}
// 2) Line‐height multiplier exactly matching Excalidraw
function getLineHeightMultiplier(fontFamily) {
    switch (fontFamily) {
        case 1: return 1.25; // Virgil
        case 3: return 1.2; // Cascadia
        case 2:
        default: return 1.25; // Helvetica/Arial
    }
}
function measureTextWithCanvas(text, fontSize, fontFamily) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    // Get font string matching Excalidraw's format
    const fontString = getFontString(fontSize, fontFamily);
    ctx.font = fontString;
    // Calculate line height
    const lineHeight = fontSize * getLineHeightMultiplier(fontFamily);
    // Split text into lines
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    // Calculate max width and total height
    let maxWidth = 0;
    for (const line of lines) {
        const metrics = ctx.measureText(line);
        if (metrics.width > maxWidth)
            maxWidth = metrics.width;
    }
    const height = lines.length * lineHeight;
    // Calculate baseline (distance from top to baseline of first line)
    let baseline;
    if ('actualBoundingBoxAscent' in ctx.measureText(lines[0] || 'M')) {
        const metrics = ctx.measureText(lines[0] || 'M');
        baseline = metrics.actualBoundingBoxAscent;
    }
    else {
        // Fallback for browsers without advanced text metrics
        baseline = fontSize * 0.8; // Approximate baseline position
    }
    return {
        width: Math.ceil(maxWidth),
        height: Math.ceil(height),
        baseline: Math.ceil(baseline),
    };
}
export function createTextElement(config) {
    const text = config.text;
    const fontSize = config.fontSize ?? 20;
    const fontFamily = config.fontFamily ?? 2;
    const textAlign = config.textAlign ?? 'center';
    const verticalAlign = config.verticalAlign ?? 'middle';
    const { baseline, height, width } = measureTextWithCanvas(text, fontSize, fontFamily);
    return {
        type: 'text',
        id: config.id || crypto.randomUUID(),
        x: config.x ?? 50,
        y: config.y ?? 50,
        width,
        height,
        angle: 0,
        strokeColor: '#000000',
        backgroundColor: 'transparent',
        fillStyle: 'hachure',
        strokeWidth: 1,
        strokeStyle: 'solid',
        roughness: 1,
        opacity: 100,
        seed: Math.floor(Math.random() * 1e6),
        groupIds: [],
        strokeSharpness: 'sharp',
        version: 1,
        versionNonce: 0,
        isDeleted: false,
        boundElements: null,
        updated: Date.now(),
        link: null,
        locked: false,
        text,
        fontSize,
        fontFamily,
        textAlign,
        verticalAlign,
        baseline,
        containerId: null,
        originalText: text,
    };
}
export function createImageElement(config) {
    return {
        type: 'image',
        fileId: config.fileId,
        id: config.id || crypto.randomUUID(),
        x: config.x || 50,
        y: config.y || 50,
        width: config.width,
        height: config.height,
        angle: 0,
        strokeColor: '#000000',
        backgroundColor: 'transparent',
        fillStyle: 'hachure',
        strokeWidth: 1,
        strokeStyle: 'solid',
        roughness: 1,
        opacity: 100,
        seed: Math.floor(Math.random() * 1e6),
        groupIds: [],
        roundness: null,
        isDeleted: false,
        boundElements: null,
        version: 1,
        versionNonce: 0,
        updated: Date.now(),
        locked: false
    };
}
export function duplicateElements(elements, offsetX = 20, offsetY = 20) {
    return elements.map((element) => ({
        ...element,
        id: crypto.randomUUID(),
        x: element.x + offsetX,
        y: element.y + offsetY,
        version: 1,
        versionNonce: 0,
        updated: Date.now(),
    }));
}
export function addElementsToExcalidraw(api, elements) {
    const elementsArray = Array.isArray(elements) ? elements : [elements];
    const existingElements = api.getSceneElementsIncludingDeleted();
    const newElements = [...existingElements, ...elementsArray];
    // Update scene with new elements
    api.updateScene({
        elements: newElements
    });
}
/**
 * Attempts to parse a given text string as JSON.
 * If successful, it returns an object with `type: "json"` and the parsed content.
 * If parsing fails (e.g., the text is not valid JSON), it returns an object
 * with `type: "string"` and the original text content.
 *
 * @param text The string to be parsed.
 * @returns A ParsedContent object, allowing for type narrowing.
 */
export function parseTextToJsonOrString(text) {
    try {
        // Attempt to parse the text as JSON
        const parsedJson = JSON.parse(text);
        // If parsing is successful and the result is an object (including arrays, which are typeof 'object')
        // and not null, we consider it valid JSON content for our purpose.
        if (typeof parsedJson === 'object' && parsedJson !== null) {
            return { type: "json", content: parsedJson };
        }
        else {
            // If JSON.parse succeeds but results in a primitive (e.g., "123", "true"),
            // we'll treat it as a plain string for this function's purpose,
            // as the user likely expects a JSON object or array.
            return { type: "string", content: text };
        }
    }
    catch (error) {
        // If JSON.parse throws an error (e.g., SyntaxError for malformed JSON),
        // the text is not valid JSON.
        return { type: "string", content: text };
    }
}
